import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { ChargeSlab } from '@/services/chargeSlab/chargeSlab.types';
import { useSearch } from '@tanstack/react-router';
import React from 'react';
import useToggleChargeDetailStatus from '../hooks/useToggleChargeDetailStatus';
import { ManageChargeSlabs } from '../ManageChargeSlabs';
import { getColumns } from './Column';

export function ChargeCategoryTable({
  data,
  isLoading,
  pagination,
  categoryName,
}: {
  data: ChargeSlab[];
  isLoading?: boolean;
  pagination?: Pagination;
  categoryName?: string;
}) {
  const search = useSearch({ from: '/(app)/configuration-management/charges/$categoryId/slabs/' });
  const { subCategoryId: categoryId } = search;
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { t } = useDynamicLocale();
  const { mutateAsync } = useToggleChargeDetailStatus();

  const columns = React.useMemo(
    () => getColumns(onOpen, t, openConfirmation, mutateAsync, categoryId, categoryName),
    [onOpen, t, openConfirmation, mutateAsync, categoryId, categoryName],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination?.totalItems ?? 0) / (pagination?.pageSize ?? 1)),
    [pagination?.totalItems, pagination?.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: 'Add New Commission Slab',
      okText: <CrumbTitle id="configurationManagement.charges.manageChargeSlabModal.submit" />,
      size: 'lg',
      modalTitle: 'configurationManagement.charges.manageChargeSlabModal.manageSlabs',
      component: <ManageChargeSlabs categoryId={categoryId} categoryName={categoryName} />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    meta: {
      title: <CrumbTitle id="configurationManagement.charges.table.withdrawalSlabs" />,
      loading: isLoading,
      actionKey: 'onAddNew',
      action: onAddNew,
      totalPage: pagination?.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.sn),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
